import React, { useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { Table } from '../../Component/Table';
import { useTranslation } from 'react-i18next';
import {
  Tag,
  Alert,
  Button,
  Space,
  Modal,
  Descriptions,
  message,
  Form,
  Row,
  Col,
  Input,
  Select,
  Tooltip,
} from 'antd';
import {
  FilterOutlined,
  EyeOutlined,
  EditOutlined,
  InfoCircleOutlined,
  StopOutlined,
  CheckCircleOutlined,
  ClearOutlined,
} from '@ant-design/icons';
import { useFilters, FilterConfig } from '../../Hooks/useFilters';
import CustomerClassificationModal from './CustomerClassificationModal';
import NameByLang from '../../Component/nameByLang';
import { useQueryClient } from '@tanstack/react-query';
import { usePermissions } from '../../contexts/PermissionContext';
import Api from '../../Network';
import { AnimatedContainer, SectionCard } from '../../Component/ui';
import './style.scss';

const Customers = () => {
  const { t } = useTranslation();
  const { hasPermission, hasAnyPermission } = usePermissions();
  const history = useHistory();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [filtersVisible, setFiltersVisible] = useState(true);
  const [classificationModalVisible, setClassificationModalVisible] = useState(false);
  const [classificationViewModalVisible, setClassificationViewModalVisible] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const confirmOpenRef = useRef(false);

  const openConfirmAction = (options: Parameters<typeof Modal.confirm>[0]) => {
    if (confirmOpenRef.current) {
      return;
    }

    confirmOpenRef.current = true;
    Modal.confirm({
      ...options,
      afterClose: (...args) => {
        confirmOpenRef.current = false;
        options.afterClose?.(...args);
      },
    });
  };

  // Define filter configuration
  const filterConfig: FilterConfig = {
    name: {
      type: 'input',
      placeholder: t('customers.enterName', 'Enter customer name'),
      label: t('customers.columns.name', 'Name'),
    },
    status: {
      type: 'select',
      label: t('customers.columns.status', 'Status'),
      options: [
        { label: t('customers.status.active', 'Active'), value: 'Active' },
        { label: t('customers.status.pending', 'Pending'), value: 'Pending' },
        { label: t('customers.status.inactive', 'Inactive'), value: 'Inactive' },
        { label: t('customers.status.pending', 'Pending'), value: 'Pending' },
      ],
    },
  };

  // Use the general filter hook
  const { filters, updateFilters, clearFilters } = useFilters();

  // Debug logging
  console.log('Customers component render:', { filtersVisible, filters });

  // Filter handlers
  const handleFilterApply = (values: any) => {
    console.log('Customers: Applying filters:', values);
    updateFilters(values);
  };

  const handleFilterClear = () => {
    form.resetFields();
    clearFilters();
  };

  const handleEditClassification = (record: any) => {
    setSelectedCustomer(record);
    setClassificationModalVisible(true);
  };

  const handleViewClassification = (record: any) => {
    setSelectedCustomer(record);
    setClassificationViewModalVisible(true);
  };

  const handleClassificationModalClose = () => {
    setClassificationModalVisible(false);
    setSelectedCustomer(null);
  };

  const handleClassificationViewModalClose = () => {
    setClassificationViewModalVisible(false);
    setSelectedCustomer(null);
  };

  const handleClassificationModalSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['customers'] });
  };

  const handleBlockCustomer = (customerId: number, customerName: string) => {
    openConfirmAction({
      title: t('customers.blockConfirm', 'Block Customer'),
      content: t('customers.blockConfirmMessage', {
        name: customerName,
        defaultValue: `Are you sure you want to block ${customerName}? They will not be able to login.`,
      }),
      okText: t('common.yes', 'Yes'),
      cancelText: t('common.no', 'No'),
      okButtonProps: { danger: true },
      onOk: async () => {
        await Api.post(`/api/admin/customers/${customerId}/block`);
        message.success(t('customers.blockSuccess', 'Customer blocked successfully'));
        queryClient.invalidateQueries({ queryKey: ['customers'] });
      },
    });
  };

  const handleApproveCustomer = (customerId: number, customerName: string) => {
    Modal.confirm({
      title: t('customers.approveConfirm', 'Approve Customer'),
      content: t('customers.approveConfirmMessage', {
        name: customerName,
        defaultValue: `Approve ${customerName} and sync to Qoyod?`,
      }),
      okText: t('common.yes', 'Yes'),
      cancelText: t('common.no', 'No'),
      onOk: async () => {
        try {
          await Api.post(`/api/admin/customers/${customerId}/change-status`, {
            status: 'Active',
          });
          message.success(t('customers.approveSuccess', 'Customer approved and synced to Qoyod'));
          queryClient.invalidateQueries({ queryKey: ['customers'] });
        } catch (error: any) {
          message.error(
            error?.response?.data?.msg ||
              error?.response?.data?.message ||
              t('customers.approveError', 'Failed to approve customer')
          );
        }
      },
    });
  };

  const handleUnblockCustomer = (customerId: number, customerName: string) => {
    openConfirmAction({
      title: t('customers.unblockConfirm', 'Unblock Customer'),
      content: t('customers.unblockConfirmMessage', {
        name: customerName,
        defaultValue: `Are you sure you want to unblock ${customerName}?`,
      }),
      okText: t('common.yes', 'Yes'),
      cancelText: t('common.no', 'No'),
      onOk: async () => {
        await Api.post(`/api/admin/customers/${customerId}/unblock`);
        message.success(t('customers.unblockSuccess', 'Customer unblocked successfully'));
        queryClient.invalidateQueries({ queryKey: ['customers'] });
      },
    });
  };

  return (
    <AnimatedContainer className="customers-page" variant="fadeUp">
      {/* Filters Card */}
      {filtersVisible && (
        <SectionCard className="premium-filter-form" style={{ marginBottom: 24 }}>
          <Form form={form} layout="vertical" onFinish={handleFilterApply} initialValues={filters}>
            <Row gutter={16} align="bottom">
              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  name="name"
                  label={filterConfig.name.label || t('customers.columns.name', 'Name')}
                >
                  <Input
                    placeholder={
                      filterConfig.name.placeholder ||
                      t('customers.enterName', 'Enter customer name')
                    }
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} lg={8}>
                <Form.Item
                  name="status"
                  label={filterConfig.status.label || t('customers.columns.status', 'Status')}
                >
                  <Select placeholder={t('customers.selectStatus', 'Select status')} allowClear>
                    {filterConfig.status.options?.map((option) => (
                      <Select.Option key={option.value} value={option.value}>
                        {option.label}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} lg={8} className="premium-filter-form__actions-col">
                <Form.Item className="premium-filter-form__actions" label=" ">
                  <Space>
                    <Button type="primary" htmlType="submit">
                      {t('common.apply', 'Apply')}
                    </Button>
                    <Button
                      type="default"
                      htmlType="button"
                      onClick={handleFilterClear}
                      icon={<ClearOutlined />}
                    >
                      {t('common.clear', 'Clear')}
                    </Button>
                  </Space>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </SectionCard>
      )}

      {(() => {
        try {
          return (
            <Table
              tableQueryKey="customers"
              tableTitle={t('customers.tableTitle', 'All Customers')}
              showSearch={true}
              showDeleteSwitch={false}
              hideFilters={true}
              rowKey="id"
              rowClassName={(record: any) => (record.blocked ? 'blocked-user-row' : '')}
              onRowClick={(record: any) => history.push(`/customers/${record.id}`)}
              tableFilter={{
                filters: filters,
                paging: { page: 1, pageSize: 20 },
                sorting: {},
              }}
              onTableFilterChange={(path: string, value: any) => {
                if (path === 'filters') {
                  updateFilters(value);
                }
              }}
              customHeaderContent={
                <Button
                  icon={<FilterOutlined />}
                  onClick={() => setFiltersVisible(!filtersVisible)}
                  type={filtersVisible ? 'primary' : 'default'}
                  style={{ marginLeft: 8 }}
                >
                  {t('customers.filters', 'Filters')}
                </Button>
              }
              columns={[
                {
                  title: t('customers.columns.id', 'ID'),
                  dataIndex: 'id',
                  sorter: false,
                  width: 80,
                  align: 'center',
                },
                {
                  title: t('customers.columns.name', 'Name'),
                  dataIndex: 'name',
                  sorter: false,
                  width: 180,
                  ellipsis: true,
                },
                {
                  title: t('customers.columns.organization', 'Company'),
                  dataIndex: 'organization',
                  sorter: false,
                  width: 180,
                  ellipsis: true,
                  render: (value: any) => value || '-',
                },
                {
                  title: t('customers.columns.tradeName', 'Trade name'),
                  dataIndex: 'trade_name',
                  sorter: false,
                  width: 160,
                  ellipsis: true,
                  render: (value: any) => value || '-',
                },
                {
                  title: t('customers.columns.phone', 'Phone'),
                  dataIndex: 'phone_number',
                  sorter: false,
                  width: 150,
                },
                {
                  title: t('customers.columns.taxNumber', 'Tax number'),
                  dataIndex: 'tax_number',
                  sorter: false,
                  width: 150,
                  ellipsis: true,
                  render: (value: any) => value || '-',
                },
                // {
                //   title: t('customers.columns.address', 'Address'),
                //   dataIndex: 'billing_address',
                //   sorter: false, // Address sorting is complex, disable for now
                //   render: (billingAddress: any) => {
                //     if (!billingAddress) return '-';
                //     const address = billingAddress.billing_address || '';
                //     const city = billingAddress.billing_city || '';
                //     return `${address} ${city}`.trim() || '-';
                //   },
                // },
                {
                  title: t('customers.columns.totalOrders', 'Total Orders'),
                  dataIndex: 'total_invoices_count',
                  sorter: false,
                  width: 120,
                  align: 'right',
                  render: (totalOrders: any) => {
                    try {
                      const count = parseInt(totalOrders);
                      return <span className="total-orders">{!isNaN(count) ? count : 0}</span>;
                    } catch (error) {
                      return <span className="total-orders">0</span>;
                    }
                  },
                },
                {
                  title: t('customers.columns.totalSpent', 'Total Spent'),
                  dataIndex: 'total_invoices_amount',
                  sorter: false,
                  width: 150,
                  align: 'right',
                  render: (totalSpent: any) => {
                    try {
                      const amount = parseFloat(totalSpent);
                      return (
                        <span className="total-spent">
                          {!isNaN(amount)
                            ? ` ${amount.toFixed(2)} ${t('common.currency', 'SAR')}`
                            : `0.00 ${t('common.currency', 'SAR')}`}
                        </span>
                      );
                    } catch (error) {
                      return (
                        <span className="total-spent">0.00 ${t('common.currency', 'SAR')}</span>
                      );
                    }
                  },
                },
                {
                  title: t('customers.columns.balance', 'Balance'),
                  dataIndex: 'closing_balance',
                  sorter: false,
                  width: 150,
                  align: 'right',
                  render: (balance: any) => {
                    try {
                      const amount = parseFloat(balance);
                      const color = amount < 0 ? 'red' : amount > 0 ? 'green' : 'default';
                      return (
                        <span
                          className="balance"
                          style={{
                            color:
                              color === 'red'
                                ? '#ff4d4f'
                                : color === 'green'
                                  ? '#52c41a'
                                  : 'inherit',
                          }}
                        >
                          {!isNaN(amount)
                            ? `${amount.toFixed(2)} ${t('common.currency', 'SAR')}`
                            : `0.00 ${t('common.currency', 'SAR')}`}
                        </span>
                      );
                    } catch (error) {
                      return <span className="balance">0.00 ${t('common.currency', 'SAR')}</span>;
                    }
                  },
                },
                {
                  title: t('customers.columns.overdue', 'Overdue'),
                  dataIndex: 'overdue',
                  sorter: false,
                  width: 150,
                  align: 'right',
                  render: (overdue: any) => {
                    try {
                      const amount = parseFloat(overdue);
                      const color = amount > 0 ? 'red' : 'default';
                      return (
                        <span
                          className="overdue"
                          style={{ color: color === 'red' ? '#ff4d4f' : 'inherit' }}
                        >
                          {!isNaN(amount)
                            ? `${amount.toFixed(2)} ${t('common.currency', 'SAR')}`
                            : `0.00 ${t('common.currency', 'SAR')}`}
                        </span>
                      );
                    } catch (error) {
                      return <span className="overdue">0.00 ${t('common.currency', 'SAR')}</span>;
                    }
                  },
                },
                {
                  title: t('customers.columns.status', 'Status'),
                  dataIndex: 'status',
                  sorter: false,
                  width: 150,
                  align: 'center',
                  render: (status: any, record: any) => {
                    if (record.blocked) {
                      return (
                        <Space direction="vertical" size={0}>
                          <Tag color="red">{t('customers.status.blocked', 'Blocked')}</Tag>
                        </Space>
                      );
                    }
                    const statusMap: { [key: string]: { color: string; text: string } } = {
                      Active: { color: 'green', text: t('customers.status.active') },
                      Inactive: { color: 'red', text: t('customers.status.inactive') },
                      Pending: {
                        color: 'orange',
                        text: t('customers.status.pending', 'Pending'),
                      },
                    };
                    const statusInfo = statusMap[status] || {
                      color: 'default',
                      text: status || 'Active',
                    };
                    return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
                  },
                },
                {
                  title: t('customers.columns.classification', 'Customer Classification'),
                  dataIndex: 'customer_classification',
                  sorter: false,
                  width: 200,
                  align: 'center',
                  render: (classification: any, record: any) => {
                    // If no classification, show "-"
                    if (!classification) {
                      return <span>-</span>;
                    }

                    // If classification exists, show view button
                    return (
                      <Tooltip title={t('customers.viewClassification', 'View Classification')}>
                        <Button
                          type="default"
                          size="small"
                          className="customers-table-btn"
                          icon={<InfoCircleOutlined />}
                          aria-label={t('customers.viewClassification', 'View Classification')}
                          onClick={() => {
                            handleViewClassification(record);
                          }}
                        />
                      </Tooltip>
                    );
                  },
                },
                // {
                //   title: t('customers.columns.createdAt', 'Created At'),
                //   dataIndex: 'created_at',
                //   sorter: false, // Backend doesn't support custom sorting
                //   render: (createdAt: any) => {
                //     if (!createdAt) return '-';
                //     try {
                //       return new Date(createdAt).toLocaleDateString();
                //     } catch (error) {
                //       return '-';
                //     }
                //   },
                // },
                {
                  title: t('customers.columns.actions', 'Actions'),
                  key: 'actions',
                  width: 140,
                  align: 'center',
                  render: (_, record: any) => {
                    // User needs explicit permission to create or update customer classifications
                    // Only these specific permissions allow editing customer classification
                    const hasCreate = hasPermission('customer_classifications.create');
                    const hasUpdate = hasPermission('customer_classifications.update');
                    const canEditClassification = hasCreate || hasUpdate;
                    const canBlockCustomer = hasAnyPermission([
                      'customers.block',
                      'block_customer',
                    ]);

                    return (
                      <div className="customers-row-actions">
                        <Tooltip title={t('customers.actions.view', 'View')}>
                          <Button
                            type="primary"
                            size="small"
                            className="customers-table-btn customers-table-btn--primary"
                            onClick={() => history.push(`/customers/${record.id}`)}
                            icon={<EyeOutlined />}
                            aria-label={t('customers.actions.view', 'View')}
                          />
                        </Tooltip>
                        {canEditClassification && (
                          <Tooltip title={t('customers.actions.editClassification', 'Edit')}>
                            <Button
                              type="default"
                              size="small"
                              className="customers-table-btn"
                              onClick={() => handleEditClassification(record)}
                              icon={<EditOutlined />}
                              aria-label={t('customers.actions.editClassification', 'Edit')}
                            />
                          </Tooltip>
                        )}
                        {record.status === 'Pending' && (
                          <Tooltip title={t('customers.actions.approve', 'Approve')}>
                            <Button
                              type="default"
                              size="small"
                              className="customers-table-btn customers-table-btn--success"
                              onClick={() => handleApproveCustomer(record.id, record.name)}
                              icon={<CheckCircleOutlined />}
                              aria-label={t('customers.actions.approve', 'Approve')}
                            />
                          </Tooltip>
                        )}
                        {canBlockCustomer &&
                          (record.blocked ? (
                            <Tooltip title={t('customers.actions.unblock', 'Unblock')}>
                              <Button
                                type="default"
                                size="small"
                                className="customers-table-btn customers-table-btn--success"
                                onClick={() => handleUnblockCustomer(record.id, record.name)}
                                icon={<CheckCircleOutlined />}
                                aria-label={t('customers.actions.unblock', 'Unblock')}
                              />
                            </Tooltip>
                          ) : (
                            <Tooltip title={t('customers.actions.block', 'Block')}>
                              <Button
                                danger
                                type="primary"
                                size="small"
                                className="customers-table-btn customers-table-btn--danger"
                                onClick={() => handleBlockCustomer(record.id, record.name)}
                                icon={<StopOutlined />}
                                aria-label={t('customers.actions.block', 'Block')}
                              />
                            </Tooltip>
                          ))}
                      </div>
                    );
                  },
                },
              ]}
              loading={false}
            />
          );
        } catch (error) {
          console.error('Error rendering customers table:', error);
          return (
            <Alert
              message="Error Rendering Table"
              description={`An error occurred while rendering the customers table: ${error}`}
              type="error"
              showIcon
            />
          );
        }
      })()}

      <CustomerClassificationModal
        visible={classificationModalVisible}
        customer={selectedCustomer}
        onClose={handleClassificationModalClose}
        onSuccess={handleClassificationModalSuccess}
      />

      {/* Classification View Modal */}
      <Modal
        title={t('customers.columns.classification', 'Customer Classification')}
        open={classificationViewModalVisible}
        onCancel={handleClassificationViewModalClose}
        footer={[
          <Button
            key="edit"
            type="primary"
            onClick={() => {
              setClassificationViewModalVisible(false);
              handleEditClassification(selectedCustomer);
            }}
          >
            {t('customers.actions.editClassification', 'Edit')}
          </Button>,
          <Button key="close" onClick={handleClassificationViewModalClose}>
            {t('common.close', 'Close')}
          </Button>,
        ]}
        width={600}
      >
        {selectedCustomer?.customer_classification ? (
          <Descriptions column={1} bordered>
            {selectedCustomer.customer_classification.customer_type && (
              <Descriptions.Item label={t('customers.columns.customerType', 'Customer Type')}>
                <NameByLang
                  name_en={selectedCustomer.customer_classification.customer_type.name_en}
                  name_ar={selectedCustomer.customer_classification.customer_type.name_ar}
                />
              </Descriptions.Item>
            )}
            {selectedCustomer.customer_classification.payment_method && (
              <Descriptions.Item label={t('customers.columns.paymentMethod', 'Payment Method')}>
                <NameByLang
                  name_en={selectedCustomer.customer_classification.payment_method.name_en}
                  name_ar={selectedCustomer.customer_classification.payment_method.name_ar}
                />
              </Descriptions.Item>
            )}
            {selectedCustomer.customer_classification.sales_representative && (
              <Descriptions.Item
                label={t('customers.columns.salesRepresentative', 'Sales Representative')}
              >
                {selectedCustomer.customer_classification.sales_representative.name}
              </Descriptions.Item>
            )}
            {(selectedCustomer.customer_classification.name_en ||
              selectedCustomer.customer_classification.name_ar) && (
              <Descriptions.Item label={t('customers.classificationName', 'Classification Name')}>
                <NameByLang
                  name_en={selectedCustomer.customer_classification.name_en}
                  name_ar={selectedCustomer.customer_classification.name_ar}
                />
              </Descriptions.Item>
            )}
            {!selectedCustomer.customer_classification.customer_type &&
              !selectedCustomer.customer_classification.payment_method &&
              !selectedCustomer.customer_classification.sales_representative && (
                <Descriptions.Item label={t('common.info', 'Info')}>
                  {t('customers.noClassificationDetails', 'No classification details available')}
                </Descriptions.Item>
              )}
          </Descriptions>
        ) : (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <p>{t('customers.noClassification', 'No classification set for this customer')}</p>
            <Button
              type="primary"
              onClick={() => {
                setClassificationViewModalVisible(false);
                handleEditClassification(selectedCustomer);
              }}
            >
              {t('customers.setClassification', 'Set Classification')}
            </Button>
          </div>
        )}
      </Modal>
    </AnimatedContainer>
  );
};

export default Customers;
